/* =========================
   RESTAURANT AR FILTERS
   Pure canvas — no external PNGs needed
   (except /chef-hat.png which you already have)
========================= */

export const FILTERS = [
  { id: "none",      label: "Off",          emoji: "✕"  },
  { id: "chefhat",   label: "Chef",         emoji: "👨‍🍳" },
  { id: "foodcrown", label: "Foodie Crown", emoji: "🍕"  },
  { id: "steam",     label: "Hot Chef",     emoji: "♨️"  },
  { id: "fork",      label: "Fork & Spoon", emoji: "🍴"  },
  { id: "stars",     label: "5 Star",       emoji: "⭐"  },
];

/* ── helpers ── */
function lm(landmarks, idx, W, H) {
  const p = landmarks[idx];
  return { x: p.x * W, y: p.y * H, z: p.z };
}
function dist(a, b) { return Math.hypot(b.x - a.x, b.y - a.y); }
function headAngle(lt, rt) {
  return Math.atan2(rt.y - lt.y, rt.x - lt.x);
}

const _imgCache = {};
function loadImage(src) {
  if (_imgCache[src]) return _imgCache[src];
  const img = new Image(); img.src = src;
  return (_imgCache[src] = img);
}

/* ================================================
   1. CHEF HAT  (uses your existing /chef-hat.png)
================================================ */
export function renderChefHat(ctx, landmarks, W, H) {
  const lt  = lm(landmarks, 127, W, H);
  const rt  = lm(landmarks, 356, W, H);
  const fh  = lm(landmarks, 10,  W, H);
  const lf  = lm(landmarks, 67,  W, H);
  const rf  = lm(landmarks, 297, W, H);
  const ch  = lm(landmarks, 152, W, H);

  const angle  = headAngle(lt, rt) * 0.55;
  const headW  = dist(lt, rt);
  const headH  = dist(fh, ch);
  const cx     = (lt.x + rt.x) / 2;
  const foreY  = (fh.y + lf.y + rf.y) / 3;
  const hatImg = loadImage("/chef-hat.png");

  if (!hatImg.complete || hatImg.naturalWidth === 0) return;

  const hatW = headW * 1.22;
  const hatH = hatW  * 0.58;

  ctx.save();
  ctx.translate(cx, foreY - headH * 0.42);
  ctx.rotate(angle);
  ctx.drawImage(hatImg, -hatW / 2, -hatH * 0.72, hatW, hatH);
  ctx.restore();
}

/* ================================================
   2. FOOD CROWN
   Pizza slice · Burger · Taco drawn on canvas
   sit like a crown above the forehead
================================================ */
export function renderFoodCrown(ctx, landmarks, W, H) {
  const lt  = lm(landmarks, 127, W, H);
  const rt  = lm(landmarks, 356, W, H);
  const fh  = lm(landmarks, 10,  W, H);
  const lf  = lm(landmarks, 67,  W, H);
  const rf  = lm(landmarks, 297, W, H);
  const ch  = lm(landmarks, 152, W, H);

  const angle  = headAngle(lt, rt);
  const headW  = dist(lt, rt);
  const headH  = dist(fh, ch);
  const cx     = (lt.x + rt.x) / 2;
  const foreY  = (fh.y + lf.y + rf.y) / 3;
  const iconSz = headW * 0.28;

  ctx.save();
  ctx.translate(cx, foreY - headH * 0.48);
  ctx.rotate(angle);

  /* gold band */
  ctx.fillStyle   = "#FFD700";
  ctx.strokeStyle = "#FFA500";
  ctx.lineWidth   = 2;
  ctx.beginPath();
  ctx.roundRect(-headW * 0.55, -iconSz * 0.18, headW * 1.1, iconSz * 0.36, 6);
  ctx.fill();
  ctx.stroke();

  /* food icons evenly spaced */
  const items = [_drawPizzaSlice, _drawBurger, _drawTaco];
  const gap   = headW * 0.38;
  items.forEach((drawFn, i) => {
    ctx.save();
    ctx.translate((i - 1) * gap, -iconSz * 0.55);
    drawFn(ctx, iconSz);
    ctx.restore();
  });

  ctx.restore();
}

function _drawPizzaSlice(ctx, sz) {
  const r = sz * 0.48;
  /* crust */
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.arc(0, 0, r, -Math.PI/2 - 0.6, -Math.PI/2 + 0.6);
  ctx.closePath();
  ctx.fillStyle = "#E8843A"; ctx.fill();
  ctx.strokeStyle = "#C05A1A"; ctx.lineWidth = 1.5; ctx.stroke();
  /* cheese layer */
  ctx.lineWidth = r * 0.22;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.82, -Math.PI/2 - 0.52, -Math.PI/2 + 0.52);
  ctx.strokeStyle = "#F5CC50"; ctx.stroke();
  /* pepperoni */
  ctx.fillStyle = "#C0392B";
  ctx.beginPath(); ctx.arc(0, -r*0.62, r*0.09, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(-r*0.2, -r*0.44, r*0.08, 0, Math.PI*2); ctx.fill();
  /* shine */
  ctx.fillStyle = "rgba(255,255,255,0.28)";
  ctx.beginPath(); ctx.arc(-r*0.1, -r*0.7, r*0.06, 0, Math.PI*2); ctx.fill();
}

function _drawBurger(ctx, sz) {
  const w = sz * 0.78, h = sz * 0.62;
  /* bun top */
  ctx.fillStyle = "#D4843C";
  ctx.beginPath();
  ctx.ellipse(0, -h*0.32, w*0.5, h*0.32, 0, Math.PI, 0);
  ctx.fill();
  /* sesame */
  ctx.fillStyle = "#F5E0A0";
  [[-w*0.18,-h*0.46],[w*0.06,-h*0.55],[w*0.22,-h*0.42]].forEach(([x,y])=>{
    ctx.beginPath(); ctx.ellipse(x,y,w*0.04,w*0.025,0.4,0,Math.PI*2); ctx.fill();
  });
  /* patty */
  ctx.fillStyle = "#7B4A1E";
  ctx.beginPath(); ctx.roundRect(-w*0.5,-h*0.08,w,h*0.22,3); ctx.fill();
  /* lettuce */
  ctx.fillStyle = "#4CAF50";
  ctx.beginPath(); ctx.ellipse(0,h*0.06,w*0.52,h*0.12,0,0,Math.PI*2); ctx.fill();
  /* bun bottom */
  ctx.fillStyle = "#E8963C";
  ctx.beginPath(); ctx.roundRect(-w*0.5,h*0.12,w,h*0.22,[0,0,6,6]); ctx.fill();
}

function _drawTaco(ctx, sz) {
  const r = sz * 0.46;
  /* shell */
  ctx.fillStyle = "#F5CC50"; ctx.strokeStyle = "#D4A020"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(0, r*0.2, r, Math.PI+0.3, -0.3); ctx.closePath();
  ctx.fill(); ctx.stroke();
  /* lettuce */
  ctx.fillStyle = "#66BB6A";
  ctx.beginPath(); ctx.ellipse(0,-r*0.05,r*0.55,r*0.18,0,0,Math.PI*2); ctx.fill();
  /* meat */
  ctx.fillStyle = "#8B4513";
  ctx.beginPath(); ctx.ellipse(0,r*0.05,r*0.42,r*0.13,0,0,Math.PI*2); ctx.fill();
  /* tomato */
  ctx.fillStyle = "#E53935";
  ctx.beginPath(); ctx.arc(-r*0.18,-r*0.1,r*0.09,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(r*0.14,-r*0.08,r*0.08,0,Math.PI*2); ctx.fill();
}

/* ================================================
   3. HOT CHEF / STEAM
   Wavy steam rises from head
   Orange glow aura around face
   HOT badge near chin
   Chef hat on top
================================================ */
let _steamT = 0;

export function renderSteam(ctx, landmarks, W, H) {
  _steamT += 0.04;

  const lt  = lm(landmarks, 127, W, H);
  const rt  = lm(landmarks, 356, W, H);
  const fh  = lm(landmarks, 10,  W, H);
  const lf  = lm(landmarks, 67,  W, H);
  const rf  = lm(landmarks, 297, W, H);
  const ch  = lm(landmarks, 152, W, H);

  const cx    = (lt.x + rt.x) / 2;
  const foreY = (fh.y + lf.y + rf.y) / 3;
  const headW = dist(lt, rt);
  const headH = dist(fh, ch);
  const angle = headAngle(lt, rt);

  ctx.save();
  ctx.translate(cx, foreY);
  ctx.rotate(angle);

  /* steam wisps — 5 columns */
  for (let i = 0; i < 5; i++) {
    const ox    = (i - 2) * headW * 0.22;
    const phase = i * 0.9;
    const ht    = headH * (0.55 + 0.15 * Math.sin(_steamT + phase));

    for (let seg = 0; seg < 6; seg++) {
      const t  = _steamT + phase + seg * 0.4;
      const sy = -(seg / 6) * ht;
      const sy2= -((seg+1) / 6) * ht;
      const w1 = Math.sin(t * 1.8) * headW * 0.06;
      const w2 = Math.sin(t * 1.8 + 0.7) * headW * 0.06;
      const a  = 0.55 - seg * 0.07;

      ctx.beginPath();
      ctx.moveTo(ox + w1, sy);
      ctx.bezierCurveTo(
        ox + w1 + headW*0.04, (sy+sy2)/2,
        ox + w2 - headW*0.04, (sy+sy2)/2,
        ox + w2, sy2
      );
      ctx.strokeStyle = `rgba(200,220,255,${a})`;
      ctx.lineWidth   = 3 - seg * 0.3;
      ctx.lineCap     = "round";
      ctx.stroke();
    }
  }

  /* orange glow aura */
  const pulse = 0.12 + 0.05 * Math.sin(_steamT * 2);
  const grad  = ctx.createRadialGradient(0, headH*0.1, headW*0.4, 0, headH*0.1, headW*0.85);
  grad.addColorStop(0,    "rgba(255,120,20,0)");
  grad.addColorStop(0.75, `rgba(255,100,10,${pulse})`);
  grad.addColorStop(1,    "rgba(255,60,0,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.ellipse(0, headH*0.1, headW*0.85, headH*0.82, 0, 0, Math.PI*2);
  ctx.fill();

  /* HOT badge */
  const badgeY = headH * 0.6;
  ctx.fillStyle   = "rgba(220,50,20,0.82)";
  ctx.beginPath();
  ctx.roundRect(-headW*0.18, badgeY, headW*0.36, headH*0.14, 6);
  ctx.fill();
  ctx.font         = `bold ${Math.round(headH*0.10)}px sans-serif`;
  ctx.fillStyle    = "#ffffff";
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("🔥 HOT", 0, badgeY + headH*0.07);

  ctx.restore();

  /* chef hat on top */
  renderChefHat(ctx, landmarks, W, H);
}

/* ================================================
   4. FORK & SPOON
   Giant fork left, giant spoon right — frame the face
   like a restaurant place setting
================================================ */
export function renderForkSpoon(ctx, landmarks, W, H) {
  const lt = lm(landmarks, 127, W, H);
  const rt = lm(landmarks, 356, W, H);
  const fh = lm(landmarks, 10,  W, H);
  const ch = lm(landmarks, 152, W, H);

  const angle  = headAngle(lt, rt);
  const headH  = dist(fh, ch);
  const headW  = dist(lt, rt);
  const midY   = (fh.y + ch.y) / 2;
  const toolH  = headH * 1.6;
  const toolW  = headH * 0.15;
  const forkX  = lt.x - headW * 0.30;
  const spoonX = rt.x + headW * 0.30;
  const handleH = toolH * 0.55;
  const tineH   = toolH * 0.38;
  const tineGap = toolW * 0.28;

  /* ── FORK ── */
  ctx.save();
  ctx.translate(forkX, midY);
  ctx.rotate(angle + 0.08);

  ctx.fillStyle   = "#C8C8C8";
  ctx.strokeStyle = "#999";
  ctx.lineWidth   = 1;
  /* handle */
  ctx.beginPath();
  ctx.roundRect(-toolW*0.22, -handleH*0.5, toolW*0.44, handleH, 8);
  ctx.fill(); ctx.stroke();
  /* tines */
  [-1.5, -0.5, 0.5, 1.5].forEach((n) => {
    ctx.beginPath();
    ctx.roundRect(n*tineGap - toolW*0.07, -handleH*0.5 - tineH, toolW*0.14, tineH, [4,4,0,0]);
    ctx.fill(); ctx.stroke();
  });
  /* tine bridge */
  ctx.beginPath();
  ctx.arc(0, -handleH*0.5 - tineH*0.28, tineGap*2.2, Math.PI, 0);
  ctx.strokeStyle = "#C8C8C8";
  ctx.lineWidth   = toolW * 0.14;
  ctx.stroke();

  ctx.restore();

  /* ── SPOON ── */
  ctx.save();
  ctx.translate(spoonX, midY);
  ctx.rotate(angle - 0.08);

  ctx.fillStyle   = "#C8C8C8";
  ctx.strokeStyle = "#999";
  ctx.lineWidth   = 1;
  /* handle */
  ctx.beginPath();
  ctx.roundRect(-toolW*0.22, -handleH*0.5, toolW*0.44, handleH, 8);
  ctx.fill(); ctx.stroke();
  /* bowl */
  const bowlR = toolW * 1.1;
  ctx.beginPath();
  ctx.ellipse(0, -handleH*0.5 - bowlR*0.7, bowlR, bowlR*1.3, 0, 0, Math.PI*2);
  ctx.fill(); ctx.stroke();
  /* shine */
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.beginPath();
  ctx.ellipse(-bowlR*0.22, -handleH*0.5 - bowlR*0.9, bowlR*0.28, bowlR*0.42, -0.4, 0, Math.PI*2);
  ctx.fill();

  ctx.restore();
}

/* ================================================
   5. FIVE STAR
   Animated gold stars float above the head
   "5 STAR FOODIE" badge near chin
================================================ */
let _starT = 0;

export function renderFiveStar(ctx, landmarks, W, H) {
  _starT += 0.03;

  const lt  = lm(landmarks, 127, W, H);
  const rt  = lm(landmarks, 356, W, H);
  const fh  = lm(landmarks, 10,  W, H);
  const lf  = lm(landmarks, 67,  W, H);
  const rf  = lm(landmarks, 297, W, H);
  const ch  = lm(landmarks, 152, W, H);

  const angle  = headAngle(lt, rt);
  const cx     = (lt.x + rt.x) / 2;
  const headW  = dist(lt, rt);
  const headH  = dist(fh, ch);
  const foreY  = (fh.y + lf.y + rf.y) / 3;

  /* stars */
  ctx.save();
  ctx.translate(cx, foreY - headH * 0.32);
  ctx.rotate(angle);

  const starSz  = headW * 0.13;
  const starGap = headW * 0.23;

  for (let i = 0; i < 5; i++) {
    const ox    = (i - 2) * starGap;
    const bob   = Math.sin(_starT + i * 0.6) * headH * 0.03;
    const scale = 0.9 + 0.1 * Math.sin(_starT * 2 + i * 0.8);
    ctx.save();
    ctx.translate(ox, bob);
    ctx.scale(scale, scale);
    _drawStar(ctx, starSz, "#FFD700", "#FFA500");
    ctx.restore();
  }

  ctx.restore();

  /* badge near chin */
  const badgeCx = cx;
  const badgeY  = ch.y + headH * 0.06;
  const badgeW  = headW * 0.78;
  const badgeH  = headH * 0.14;

  ctx.save();
  ctx.translate(badgeCx, badgeY);
  ctx.rotate(angle);

  ctx.fillStyle   = "rgba(15,15,15,0.78)";
  ctx.strokeStyle = "#FFD700";
  ctx.lineWidth   = 2;
  ctx.beginPath();
  ctx.roundRect(-badgeW/2, 0, badgeW, badgeH, badgeH/2);
  ctx.fill(); ctx.stroke();

  ctx.font         = `bold ${Math.round(badgeH*0.62)}px sans-serif`;
  ctx.fillStyle    = "#FFD700";
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("⭐ 5 STAR FOODIE ⭐", 0, badgeH * 0.52);

  ctx.restore();
}

function _drawStar(ctx, r, fill, stroke) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a   = (i * Math.PI) / 5 - Math.PI / 2;
    const x   = Math.cos(a) * rad, y = Math.sin(a) * rad;
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = fill; ctx.fill();
  ctx.strokeStyle = stroke; ctx.lineWidth = 1.5; ctx.stroke();
  /* shine */
  ctx.fillStyle = "rgba(255,255,255,0.40)";
  ctx.beginPath(); ctx.arc(-r*0.18, -r*0.28, r*0.22, 0, Math.PI*2); ctx.fill();
}

/* ================================================
   DISPATCHER
================================================ */
export function renderFilter(ctx, filterId, landmarks, W, H) {
  switch (filterId) {
    case "chefhat":   renderChefHat(ctx, landmarks, W, H);   break;
    case "foodcrown": renderFoodCrown(ctx, landmarks, W, H); break;
    case "steam":     renderSteam(ctx, landmarks, W, H);     break;
    case "fork":      renderForkSpoon(ctx, landmarks, W, H); break;
    case "stars":     renderFiveStar(ctx, landmarks, W, H);  break;
    default: break;
  }
}